import { useHandler } from "@redotech/react-util/hook";
import { useDebouncedLoad } from "@redotech/react-util/load";
import {
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  RefObject,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface RedoTextareaValidationIssue {
  /** Inclusive start offset into the validated text. */
  start: number;
  /** Exclusive end offset into the validated text. */
  end: number;
  message: string;
  /** When set, a Fix action replaces the flagged range with this text. */
  replacement?: string;
  /** The caller's own label for the issue, passed back to `resolveFix`. */
  kind?: string;
}

/** An edit to apply, with offsets into the text the fix was written for. */
export interface RedoTextareaFix {
  start: number;
  end: number;
  replacement: string;
}

/** Handed to `onFix`, so the caller can show its own fix UI and act on the text. */
export interface RedoTextareaFixRequest {
  /** The flagged highlight, for placing UI beside it. */
  anchor: HTMLElement | null;
  /** Applies an edit to the current text, which isn't re-checked. */
  applyEdit(edit: RedoTextareaFix): void;
  /** Clears the highlight, for an issue resolved outside this text. */
  dismiss(): void;
  /** Returns focus to the text, for when the caller's fix UI closes. */
  focus(): void;
}

export type RedoTextareaValidationStatus =
  | "idle"
  | "loading"
  | "valid"
  | "invalid";

export interface RedoTextareaValidation {
  /** Called after typing pauses. An empty result marks the text as valid. */
  validate(
    value: string,
    signal: AbortSignal,
  ): Promise<readonly RedoTextareaValidationIssue[]>;
  /** Writes a fix when Fix is clicked on an issue without a `replacement`; null when there is none. */
  resolveFix?(
    value: string,
    issue: RedoTextareaValidationIssue,
    signal: AbortSignal,
  ): Promise<RedoTextareaFix | null>;
  /** When set, Fix hands the issue to the caller instead of editing the text. */
  onFix?(
    issue: RedoTextareaValidationIssue,
    request: RedoTextareaFixRequest,
  ): void;
  debounceMs?: number;
  onStatusChange?(status: RedoTextareaValidationStatus): void;
}

const DEFAULT_DEBOUNCE_MS = 500;

/**
 * Moves issues found in `validatedText` onto `currentText`, dropping any the edit touched.
 * Returns sorted, in-bounds, non-overlapping issues.
 */
export function alignIssuesToText<T extends RedoTextareaValidationIssue>(
  issues: readonly T[],
  validatedText: string,
  currentText: string,
): T[] {
  const maxPrefix = Math.min(validatedText.length, currentText.length);
  let prefix = 0;
  while (prefix < maxPrefix && validatedText[prefix] === currentText[prefix]) {
    prefix++;
  }
  let suffix = 0;
  while (
    suffix < maxPrefix - prefix &&
    validatedText[validatedText.length - 1 - suffix] ===
      currentText[currentText.length - 1 - suffix]
  ) {
    suffix++;
  }
  const editEnd = validatedText.length - suffix;
  const delta = currentText.length - validatedText.length;

  const aligned = issues
    .filter(
      (issue) =>
        issue.start >= 0 &&
        issue.start < issue.end &&
        issue.end <= validatedText.length,
    )
    .flatMap((issue) => {
      if (issue.end <= prefix) {
        return [issue];
      }
      if (issue.start >= editEnd) {
        return [
          { ...issue, start: issue.start + delta, end: issue.end + delta },
        ];
      }
      return [];
    })
    .sort((a, b) => a.start - b.start);

  let lastEnd = 0;
  return aligned.filter((issue) => {
    if (issue.start < lastEnd) {
      return false;
    }
    lastEnd = issue.end;
    return true;
  });
}

/** Identifies one flagged occurrence (the nth repeat of its text) so Ignore survives re-validation. */
export function issueIgnoreKey(
  issue: RedoTextareaValidationIssue,
  text: string,
): string {
  const flagged = text.slice(issue.start, issue.end);
  const occurrence = text.slice(0, issue.start).split(flagged).length - 1;
  return `${issue.message}\u0000${flagged}\u0000${occurrence}`;
}

export function useTextareaValidation(
  value: string,
  validation: RedoTextareaValidation | undefined,
): {
  status: RedoTextareaValidationStatus;
  issues: RedoTextareaValidationIssue[];
  /** True once any result exists; stays true while the next run is pending. */
  checked: boolean;
  ignore(issue: RedoTextareaValidationIssue): void;
  /** Text produced by a Fix isn't re-checked unless it had unchecked edits; the remaining issues just shift. */
  markFixed(text: string): void;
} {
  const [ignoredKeys, setIgnoredKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [fixed, setFixed] = useState<{
    text: string;
    issues: RedoTextareaValidationIssue[];
    /** The validation result the fixed issues were carried over from. */
    from: unknown;
    /** False when the text before the Fix had unchecked edits. */
    checked: boolean;
  } | null>(null);
  const shouldValidate = !!validation && value.trim() !== "";
  const shouldRequest =
    shouldValidate && !(fixed?.checked && value === fixed.text);
  // The text a request went out for; until it matches, the debounce is still waiting.
  const [requestedText, setRequestedText] = useState<string | null>(null);

  const load = useDebouncedLoad(
    async (signal) => {
      if (!validation || !shouldRequest) {
        return undefined;
      }
      setRequestedText(value);
      return { text: value, issues: await validation.validate(value, signal) };
    },
    validation?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
    [value],
    shouldRequest,
  );
  const result = load.value;
  const requestInFlight = load.pending && requestedText === value;

  const base =
    fixed && result && fixed.from === result ? fixed : (result ?? null);
  const alignedIssues = useMemo(
    () =>
      shouldValidate && base
        ? alignIssuesToText(base.issues, base.text, value)
        : [],
    [shouldValidate, base, value],
  );
  const issues = useMemo(
    () =>
      alignedIssues.filter(
        (issue) => !ignoredKeys.has(issueIgnoreKey(issue, value)),
      ),
    [alignedIssues, value, ignoredKeys],
  );

  // While typing, keep showing the issues still in the text, but not a check
  // the new text hasn't earned yet.
  const status: RedoTextareaValidationStatus = !shouldValidate
    ? "idle"
    : requestInFlight
      ? "loading"
      : issues.length > 0
        ? "invalid"
        : load.pending || !result
          ? "idle"
          : "valid";

  const notifyStatus = useHandler((status: RedoTextareaValidationStatus) =>
    validation?.onStatusChange?.(status),
  );
  useEffect(() => {
    notifyStatus(status);
  }, [status, notifyStatus]);

  const ignore = useHandler((issue: RedoTextareaValidationIssue) =>
    setIgnoredKeys((keys) => new Set(keys).add(issueIgnoreKey(issue, value))),
  );

  // Carried over one edit at a time, since a single diff across a typed edit
  // and the Fix would drop the issues between them.
  const markFixed = useHandler((text: string) => {
    if (!result || !base) {
      return;
    }
    setFixed({
      text,
      issues: alignIssuesToText(alignedIssues, value, text),
      from: result,
      checked: value === base.text,
    });
  });

  return {
    status,
    issues,
    checked: shouldValidate && !!result,
    ignore,
    markFixed,
  };
}

const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 250;

/** Which issue's popover is open (from the caret or the pointer) and how focus moves through it. */
export function useTextareaIssuePopover({
  value,
  setValue,
  issues,
  ignore,
  textareaRef,
  onBlur,
  onKeyDown,
  resolveFix,
  onFix,
  getAnchor,
  onFixApplied,
}: {
  value: string;
  setValue(value: string): void;
  issues: readonly RedoTextareaValidationIssue[];
  ignore(issue: RedoTextareaValidationIssue): void;
  textareaRef: RefObject<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  resolveFix?: RedoTextareaValidation["resolveFix"];
  onFix?: RedoTextareaValidation["onFix"];
  /** The open issue's highlight, passed to `onFix`. */
  getAnchor?(): HTMLElement | null;
  onFixApplied?(text: string): void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const issuesRef = useRef(issues);
  issuesRef.current = issues;
  const fixAbort = useRef<AbortController>();
  useEffect(() => () => fixAbort.current?.abort(), []);
  const [fixingIssue, setFixingIssue] = useState<
    RedoTextareaValidationIssue | undefined
  >();
  const [failedFixIssue, setFailedFixIssue] = useState<
    RedoTextareaValidationIssue | undefined
  >();
  const [caret, setCaret] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [hoveredIssue, setHoveredIssue] = useState<
    RedoTextareaValidationIssue | undefined
  >();
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const caretIssue =
    caret === null || dismissed
      ? undefined
      : issues.find((issue) => issue.start <= caret && caret <= issue.end);
  const hovered =
    hoveredIssue && issues.includes(hoveredIssue) ? hoveredIssue : undefined;
  const issue = hovered ?? caretIssue;

  const close = useHandler(() => {
    clearTimeout(hoverTimer.current);
    setHoveredIssue(undefined);
    setDismissed(true);
  });

  const returnFocus = useHandler(() => {
    close();
    textareaRef.current?.focus();
  });

  const focusIsInside = useHandler(() => {
    const active = document.activeElement;
    return (
      active === textareaRef.current ||
      (!!active && !!popoverRef.current?.contains(active))
    );
  });

  const hoverIssue = useHandler(
    (next: RedoTextareaValidationIssue | undefined) => {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(
        () => setHoveredIssue(next),
        next ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS,
      );
    },
  );

  const handleTextareaSelect = useHandler(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      const { selectionStart, selectionEnd } = event.currentTarget;
      const next = selectionStart === selectionEnd ? selectionStart : null;
      if (next !== caret) {
        setDismissed(false);
      }
      setCaret(next);
    },
  );

  const handleTextareaBlur = useHandler(
    (event: FocusEvent<HTMLTextAreaElement>) => {
      const target = event.relatedTarget;
      if (!(target instanceof Node && popoverRef.current?.contains(target))) {
        setCaret(null);
      }
      onBlur?.(event);
    },
  );

  const handleTextareaKeyDown = useHandler(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      const firstButton = popoverRef.current?.querySelector("button");
      if (issue && event.key === "Escape") {
        event.stopPropagation();
        close();
      } else if (event.key === "Tab" && !event.shiftKey && firstButton) {
        event.preventDefault();
        firstButton.focus();
      }
      onKeyDown?.(event);
    },
  );

  const applyFix = useHandler((edit: RedoTextareaFix, hadFocus: boolean) => {
    const current = valueRef.current;
    const next =
      current.slice(0, edit.start) + edit.replacement + current.slice(edit.end);
    const nextCaret = edit.start + edit.replacement.length;
    onFixApplied?.(next);
    setValue(next);
    close();
    if (hadFocus) {
      textareaRef.current?.focus();
      requestAnimationFrame(() =>
        textareaRef.current?.setSelectionRange(nextCaret, nextCaret),
      );
    }
  });

  const fix = useHandler(async (target: RedoTextareaValidationIssue) => {
    const hadFocus = focusIsInside();
    setFailedFixIssue(undefined);
    if (target.replacement !== undefined) {
      applyFix(
        {
          start: target.start,
          end: target.end,
          replacement: target.replacement,
        },
        hadFocus,
      );
      return;
    }
    if (onFix) {
      const flagged = valueRef.current.slice(target.start, target.end);
      const anchor = getAnchor?.() ?? null;
      close();
      onFix(target, {
        anchor,
        applyEdit: (edit) => applyFix(edit, false),
        // The text may have moved since, so find the issue where it is now.
        dismiss: () => {
          const current = issuesRef.current.find(
            (issue) =>
              issue.message === target.message &&
              valueRef.current.slice(issue.start, issue.end) === flagged,
          );
          if (current) {
            ignore(current);
          }
        },
        focus: () => textareaRef.current?.focus(),
      });
      return;
    }
    if (!resolveFix) {
      return;
    }
    fixAbort.current?.abort();
    const controller = new AbortController();
    fixAbort.current = controller;
    const requestedValue = valueRef.current;
    setFixingIssue(target);
    try {
      const edit = await resolveFix(requestedValue, target, controller.signal);
      // A fix written for text the merchant has since changed would land in the wrong place.
      if (controller.signal.aborted || valueRef.current !== requestedValue) {
        return;
      }
      if (edit) {
        applyFix(edit, hadFocus);
      } else {
        setFailedFixIssue(target);
      }
    } catch {
      if (!controller.signal.aborted) {
        setFailedFixIssue(target);
      }
    } finally {
      if (fixAbort.current === controller) {
        setFixingIssue(undefined);
      }
    }
  });

  const ignoreAndClose = useHandler((ignored: RedoTextareaValidationIssue) => {
    const hadFocus = focusIsInside();
    ignore(ignored);
    close();
    if (hadFocus) {
      textareaRef.current?.focus();
    }
  });

  const handlePopoverKeyDown = useHandler(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const buttons = Array.from(
        popoverRef.current?.querySelectorAll("button") ?? [],
      );
      const edge = event.shiftKey ? buttons[0] : buttons[buttons.length - 1];
      if (event.key === "Escape") {
        event.stopPropagation();
        returnFocus();
      } else if (
        event.key === "Tab" &&
        edge &&
        document.activeElement === edge
      ) {
        event.preventDefault();
        returnFocus();
      }
    },
  );

  const handlePopoverBlur = useHandler((event: FocusEvent<HTMLDivElement>) => {
    const target = event.relatedTarget;
    const staysInside =
      target instanceof Node &&
      (target === textareaRef.current || popoverRef.current?.contains(target));
    if (!staysInside) {
      setCaret(null);
      close();
    }
  });

  const keepHoverOpen = useHandler(() => clearTimeout(hoverTimer.current));
  const releaseHover = useHandler(() => hoverIssue(undefined));

  const popoverHandlers = useMemo(
    () => ({
      onFix: fix,
      onIgnore: ignoreAndClose,
      onKeyDown: handlePopoverKeyDown,
      onBlur: handlePopoverBlur,
      onMouseEnter: keepHoverOpen,
      onMouseLeave: releaseHover,
    }),
    [
      fix,
      ignoreAndClose,
      handlePopoverKeyDown,
      handlePopoverBlur,
      keepHoverOpen,
      releaseHover,
    ],
  );

  return {
    issue,
    caretIssue,
    popoverRef,
    hoverIssue,
    fixingIssue,
    failedFixIssue,
    textareaHandlers: {
      onSelect: handleTextareaSelect,
      onBlur: handleTextareaBlur,
      onKeyDown: handleTextareaKeyDown,
    },
    popoverHandlers,
  };
}
